/**
 * QuestionScopeSelector  (DA-2184)
 *
 * The forward-guard authoring control: one TARGET-only picker at the question level that
 * every choice's READ-intent editor then locks to. HP picks an attribute column, AP an
 * appliance id — no operator, no value; those stay free per choice. Clearing the picker
 * removes the scope (onChange(undefined)), which unlocks the choice pickers again.
 *
 * The scope is a convenience/consistency rail, not a stored field: it's reverse-populated
 * from the choices when unset (see SurveyQuestionForm.scopeFromLinks) and never itself
 * reaches the save payload — the choices' clauses do.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import { SearchableSelect } from 'components/common/SearchableSelect'
import { MapperCatalog } from 'types/apis/surveyInterfaces'
import { MapperScopeConstraint } from './cascadeTypes'
import { MultiValueSelect } from './MultiValueSelect'
import { hpColumnOptions } from './hpTag'
import { applianceOptions } from './apCatalog'

export interface QuestionScopeSelectorProps {
  profileCode: 'HP' | 'AP'
  value?: MapperScopeConstraint
  catalog?: MapperCatalog
  loading?: boolean
  /** Catalog fetch failure — surfaced inline so an empty picker isn't misread as "no targets". */
  error?: string
  /**
   * DA-2184: AP only — the appliance ids the question's SURVEY CATEGORY maps to
   * (survey-category.applianceIds). When set, the appliance picker is restricted to these, so an
   * author can only scope appliances that belong to the category. Empty/undefined = no category
   * restriction (falls back to the full catalog, so categories without a configured list are
   * unaffected).
   */
  applianceAllowlist?: number[]
  onChange: (scope: MapperScopeConstraint | undefined) => void
}

export const QuestionScopeSelector: React.FC<QuestionScopeSelectorProps> = ({
  profileCode,
  value,
  catalog,
  loading,
  error,
  applianceAllowlist,
  onChange,
}) => {
  const isHp = profileCode === 'HP'

  // HP scopes a single attribute column; AP scopes a SET of appliance ids so a choice can
  // build an `&`/`|` group from them (e.g. AP:502|505|506|507|508:A:CONTROLLER-timebased).
  const hpOptions = hpColumnOptions(catalog?.homeProfileAttributes)
  const allApOptions = applianceOptions(catalog?.appliances)
  // Restrict to the category's appliance ids when the category has a configured list.
  const apOptions = applianceAllowlist?.length
    ? allApOptions.filter((o) => applianceAllowlist.includes(Number(o.value)))
    : allApOptions
  const options = isHp ? hpOptions : apOptions

  const handleHpChange = (v: string) => onChange(v ? { profileCode: 'HP', attributePath: v } : undefined)
  const handleApChange = (vals: string[]) => {
    const ids = vals.map(Number)
    onChange(ids.length ? { profileCode: 'AP', applianceIds: ids } : undefined)
  }

  // Distinguish fetch-failure from a legitimately-empty pilot: both leave the picker empty, but
  // only one is an error. The message rides on the control so it's aria-describedby-linked.
  const isEmpty = !loading && !error && options.length === 0
  const emptyText = isHp
    ? 'No attributes available for this pilot'
    : applianceAllowlist?.length
    ? 'No appliances from this category are in the catalog'
    : 'No appliances available for this pilot'
  const helperText = error || (isEmpty ? emptyText : undefined)
  const noOptionsText = error ? 'Couldn’t load catalog' : isHp ? 'No attributes available' : 'No appliances available'

  return (
    <Box sx={{ maxWidth: 560 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
        Mapper Scope
      </Typography>
      {isHp ? (
        <SearchableSelect
          label="Attribute (applies to all choices)"
          value={value?.attributePath ?? ''}
          onChange={handleHpChange}
          options={hpOptions}
          fullWidth
          loading={loading}
          error={!!error}
          helperText={helperText}
          noOptionsText={noOptionsText}
          placeholder={loading ? 'Loading catalog…' : 'Select an attribute'}
        />
      ) : (
        <MultiValueSelect
          label="Appliance(s) (applies to all choices)"
          values={(value?.applianceIds ?? []).map(String)}
          onChange={handleApChange}
          options={apOptions}
          loading={loading}
          error={!!error}
          helperText={helperText}
          noOptionsText={noOptionsText}
          placeholder={loading ? 'Loading catalog…' : 'Select one or more appliances'}
        />
      )}
      <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: 'text.secondary' }}>
        Restricts each choice's target{isHp ? ' attribute' : ' appliance(s)'}; operator and value stay free per choice
        {isHp ? '' : ', and a choice may combine the scoped appliances into an & / | group'}. Clear to unlock.
      </Typography>
    </Box>
  )
}
